// src/utils/analyticsUtils.js - COMPLETE VERSION MET CORRECTE CUSTOMER TRACKING

import { identifyNiche, extractCustomerCode, getDefaultPrice } from './dashboardUtils';
import { LEAD_CLASSIFICATIONS, NON_LEAD_CLASSIFICATIONS, CLASSIFICATION_EMOJIS, isLeadClassificationForAdmin } from '../constants/businessLogic';
import { getPriceForDate } from './supabaseHelpers';
import { getWeekStart, getMonthStart, formatTrendLabel } from './dateUtils';

export const processCallsToAnalytics = (calls, customerTargets = [], useAdminLeadLogic = false, priceMap = null) => {
  if (!calls.length) return {
    totalRevenue: 0,
    totalCalls: 0,
    leadCalls: 0,
    nonLeadCalls: 0,
    totalUnique: 0,
    campaigns: [],
    niches: [],
    classifications: [],
    trends: [],
    customers: []
  };

  const customerLookup = {};
  customerTargets.forEach(target => {
    customerLookup[target.customer_code] = target;
  });

  let totalRevenue = 0;
  let totalCalls = calls.length;
  let leadCalls = 0;
  let nonLeadCalls = 0;

  // Eigen unique-telling (sinds 24 mei 2026):
  // COUNT(DISTINCT (campaign, from_number)) per filter-periode.
  // Een beller die naar 2 verschillende campagnes belt = 2 uniek.
  // Een beller die 5x naar zelfde campagne belt = 1 uniek voor die campagne.
  // Niet meer afhankelijk van Qooqie's lifetime is_recurring vlag.
  const totalUniqueSet = new Set();

  const campaignStats = {};
  const nicheStats = {};
  const classificationStats = {};
  const customerStats = {};
  const dailyStats = {};

  calls.forEach(call => {
    const classification = call.classification || '';
    const campaignName = call.campaign || '';
    const niche = identifyNiche(campaignName);
    const customerCode = extractCustomerCode(campaignName);
    // Use Amsterdam timezone for date grouping (not UTC)
    const callDate = new Date(call.start).toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });

    // Composite key: 1 uniek per (campagne, from-nummer) combinatie per periode
    // Anonymous calls krijgen unieke suffix (call.start) zodat elke anonymous billable
    // als +1 uniek telt; we kunnen ze niet ontdubbelen zonder identiteit.
    const fromKey = call.from && call.from !== 'anonymous'
      ? call.from
      : (call.from === 'anonymous' ? `anonymous:${call.start}` : null);
    const uniqKey = fromKey && campaignName ? `${campaignName}|${fromKey}` : null;

    // Initialize stats objects
    if (!nicheStats[niche]) {
      nicheStats[niche] = {
        name: niche,
        calls: 0,
        leadCalls: 0,
        unique: 0,
        uniqueSet: new Set(),
        revenue: 0,
        customers: new Set()
      };
    }

    if (!classificationStats[classification]) {
      classificationStats[classification] = {
        type: classification,
        count: 0,
        emoji: CLASSIFICATION_EMOJIS[classification] || '📞'
      };
    }

    if (!dailyStats[callDate]) {
      dailyStats[callDate] = {
        date: callDate,
        calls: 0,
        revenue: 0,
        leadCalls: 0,
        unique: 0,
        uniqueSet: new Set()
      };
    }

    // Update counts
    nicheStats[niche].calls++;
    classificationStats[classification].count++;
    dailyStats[callDate].calls++;

    // Eigen unique-telling: (campaign, from) tuple via composite key
    if (uniqKey) {
      totalUniqueSet.add(uniqKey);
      dailyStats[callDate].uniqueSet.add(uniqKey);
      nicheStats[niche].uniqueSet.add(uniqKey);
    }

    // Process revenue for lead calls
    const isLead = useAdminLeadLogic 
      ? isLeadClassificationForAdmin(classification, call.is_recurring)
      : LEAD_CLASSIFICATIONS.includes(classification);
    
    // Initialize customer stats for ALL calls (not just leads/non-leads)
    const customer = customerCode ? customerLookup[customerCode] : null;
    const customerName = customer ? customer.name : customerCode || 'Unknown';
    
    if (!customerStats[customerName]) {
      customerStats[customerName] = {
        name: customerName,
        code: customerCode,
        totalCalls: 0,
        leadCalls: 0,
        nonLeadCalls: 0,
        unique: 0,
        uniqueSet: new Set(),
        revenue: 0
      };
    }

    // Always increment total calls for this customer
    customerStats[customerName].totalCalls++;
    if (uniqKey) {
      customerStats[customerName].uniqueSet.add(uniqKey);
    }

    if (isLead) {
      leadCalls++;
      nicheStats[niche].leadCalls++;
      dailyStats[callDate].leadCalls++;
      customerStats[customerName].leadCalls++;

      // ALLEEN REVENUE BEREKENEN VOOR NEDERLANDSE NICHES
      if (niche === 'Ontstoppen' || niche === 'Loodgieter' || niche === 'Dakdekker') {
        // Calculate revenue - check date-based price first, then fallback
        const dateBasedPrice = priceMap && customerCode
          ? getPriceForDate(priceMap, customerCode, call.start)
          : null;
        const price = dateBasedPrice ?? (customer ? customer.price : getDefaultPrice(niche));
        const revenue = price || 0;

        totalRevenue += revenue;
        nicheStats[niche].revenue += revenue;
        dailyStats[callDate].revenue += revenue;
        customerStats[customerName].revenue += revenue;

        // CORRECTE CUSTOMER TRACKING: Alleen voor revenue-generating calls
        nicheStats[niche].customers.add(customerName);
      }
    } else if (NON_LEAD_CLASSIFICATIONS.includes(classification)) {
      nonLeadCalls++;
      customerStats[customerName].nonLeadCalls++;
    }
  });

  // Convert dailyStats naar trends array met unique property en dayOfWeek
  const trends = Object.values(dailyStats)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(day => {
      // Parse date as Amsterdam timezone to get correct day of week
      // Use noon UTC and resolve weekday via Europe/Amsterdam to avoid DST edge cases
      const dateObj = new Date(day.date + 'T12:00:00Z');
      const amsWeekdayShort = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Europe/Amsterdam' });
      const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      const jsDayOfWeek = weekdayMap[amsWeekdayShort] ?? dateObj.getUTCDay();
      // Convert to Monday=0, Tuesday=1, ..., Sunday=6
      const dayOfWeek = jsDayOfWeek === 0 ? 6 : jsDayOfWeek - 1;
      return {
        date: day.date,
        dayOfWeek,
        calls: day.calls,
        leadCalls: day.leadCalls,
        revenue: day.revenue,
        unique: day.uniqueSet ? day.uniqueSet.size : (day.unique || 0)
      };
    });

  return {
    totalRevenue,
    totalCalls,
    leadCalls,
    nonLeadCalls,
    totalUnique: totalUniqueSet.size,
    campaigns: Object.values(campaignStats),
    niches: Object.values(nicheStats).filter(n => n.calls > 0).map(n => ({
      ...n,
      unique: n.uniqueSet ? n.uniqueSet.size : 0,
      uniqueSet: undefined,
      customers: n.customers.size // Convert Set to number
    })),
    classifications: Object.values(classificationStats).filter(c => c.count > 0),
    trends,
    customers: Object.values(customerStats).map(c => ({
      ...c,
      unique: c.uniqueSet ? c.uniqueSet.size : (c.unique || 0),
      uniqueSet: undefined
    }))
  };
};

export const combinePeriodsForTrends = (currentTrends, previousTrends, period = null) => {
  // Day names for week periods (Dutch)
  const dayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

  // Check if this is a week period (7 days or less with dayOfWeek data)
  const isWeekPeriod = period === 'thisWeek' || period === 'lastWeek' ||
    (currentTrends.length <= 7 && currentTrends.every(t => t.dayOfWeek !== undefined));

  if (isWeekPeriod && currentTrends.length > 0) {
    // For week periods: align by day of week (Monday=0 to Sunday=6)
    // Create a map of previous trends by dayOfWeek
    const previousByDayOfWeek = new Map();
    previousTrends.forEach(trend => {
      if (trend.dayOfWeek !== undefined) {
        previousByDayOfWeek.set(trend.dayOfWeek, trend);
      }
    });

    // Combine using dayOfWeek alignment
    const combinedTrends = currentTrends.map(currentTrend => {
      const previousTrend = previousByDayOfWeek.get(currentTrend.dayOfWeek) || null;

      return {
        date: currentTrend.date,
        dayOfWeek: currentTrend.dayOfWeek,
        dayName: dayNames[currentTrend.dayOfWeek],
        revenue: currentTrend.revenue || 0,
        calls: currentTrend.calls || 0,
        leadCalls: currentTrend.leadCalls || 0,
        unique: currentTrend.unique || 0,
        previousRevenue: previousTrend?.revenue || 0,
        previousCalls: previousTrend?.calls || 0,
        previousLeadCalls: previousTrend?.leadCalls || 0,
        previousUnique: previousTrend?.unique || 0
      };
    });

    return combinedTrends;
  }

  // For non-week periods: use index-based mapping (original behavior)
  const combinedTrends = currentTrends.map((currentTrend, index) => {
    const previousTrend = previousTrends[index] || null;

    return {
      date: currentTrend.date,
      dayOfWeek: currentTrend.dayOfWeek,
      dayName: currentTrend.dayOfWeek !== undefined ? dayNames[currentTrend.dayOfWeek] : null,
      revenue: currentTrend.revenue || 0,
      calls: currentTrend.calls || 0,
      leadCalls: currentTrend.leadCalls || 0,
      unique: currentTrend.unique || 0,
      previousRevenue: previousTrend?.revenue || 0,
      previousCalls: previousTrend?.calls || 0,
      previousLeadCalls: previousTrend?.leadCalls || 0,
      previousUnique: previousTrend?.unique || 0
    };
  });

  return combinedTrends;
};

export const applyCumulativeView = (trends) => {
  let cumulativeRevenue = 0;
  let cumulativeCalls = 0;
  let cumulativeLeadCalls = 0;
  let cumulativeUnique = 0;
  let cumulativePrevRevenue = 0;
  let cumulativePrevCalls = 0;
  let cumulativePrevLeadCalls = 0;
  let cumulativePrevUnique = 0;

  return trends.map(trend => {
    cumulativeRevenue += trend.revenue;
    cumulativeCalls += trend.calls;
    cumulativeLeadCalls += trend.leadCalls;
    cumulativeUnique += trend.unique;
    cumulativePrevRevenue += trend.previousRevenue;
    cumulativePrevCalls += trend.previousCalls;
    cumulativePrevLeadCalls += trend.previousLeadCalls;
    cumulativePrevUnique += trend.previousUnique;

    return {
      ...trend,
      revenue: cumulativeRevenue,
      calls: cumulativeCalls,
      leadCalls: cumulativeLeadCalls,
      unique: cumulativeUnique,
      previousRevenue: cumulativePrevRevenue,
      previousCalls: cumulativePrevCalls,
      previousLeadCalls: cumulativePrevLeadCalls,
      previousUnique: cumulativePrevUnique
    };
  });
};

/**
 * Aggregate daily trends by granularity (week or month)
 * @param {Array} dailyTrends - Array of daily trend objects
 * @param {'day' | 'week' | 'month'} granularity - The aggregation level
 * @returns {Array} - Aggregated trends with labels
 */
export const aggregateTrendsByGranularity = (dailyTrends, granularity) => {
  if (!dailyTrends || dailyTrends.length === 0) return [];

  // For day granularity, just add labels and return
  if (granularity === 'day') {
    return dailyTrends.map(trend => ({
      ...trend,
      label: formatTrendLabel(trend.date, 'day'),
      periodKey: trend.date
    }));
  }

  // Group by period (week or month)
  const periodStats = {};

  dailyTrends.forEach(trend => {
    const date = new Date(trend.date + 'T12:00:00');
    const periodKey = granularity === 'week'
      ? getWeekStart(date)
      : getMonthStart(date);

    if (!periodStats[periodKey]) {
      periodStats[periodKey] = {
        periodKey,
        label: formatTrendLabel(periodKey, granularity),
        calls: 0,
        leadCalls: 0,
        unique: 0,
        revenue: 0
      };
    }

    const stats = periodStats[periodKey];
    stats.calls += trend.calls || 0;
    stats.leadCalls += trend.leadCalls || 0;
    stats.unique += trend.unique || 0;
    stats.revenue += trend.revenue || 0;
  });

  // Sort by period key and return
  return Object.values(periodStats)
    .sort((a, b) => a.periodKey.localeCompare(b.periodKey));
};

/**
 * Combine current trends with previous period trends for comparison
 * Aligns by index (day 1 with day 1, week 1 with week 1, etc.)
 * @param {Array} currentTrends - Aggregated current period trends
 * @param {Array|null} previousTrends - Aggregated previous period trends
 * @returns {Array} - Combined trends with previous* fields populated
 */
export const combineTrendsWithPrevious = (currentTrends, previousTrends) => {
  if (!currentTrends || currentTrends.length === 0) return [];
  if (!previousTrends || previousTrends.length === 0) {
    // No previous data, just add zero values for previous fields
    return currentTrends.map(trend => ({
      ...trend,
      previousCalls: 0,
      previousLeadCalls: 0,
      previousUnique: 0,
      previousRevenue: 0
    }));
  }

  // Align by index (day 1 current with day 1 previous, etc.)
  return currentTrends.map((currentTrend, index) => {
    const previousTrend = previousTrends[index] || null;

    return {
      ...currentTrend,
      previousCalls: previousTrend?.calls || 0,
      previousLeadCalls: previousTrend?.leadCalls || 0,
      previousUnique: previousTrend?.unique || 0,
      previousRevenue: previousTrend?.revenue || 0
    };
  });
};
